// janus-engine/actions/weapon-attack-dice.ts — the dice half of A2: the roll source, the adv/dis roller,
// the exploding-dice chain and the three critical-hit models.
//
// Split out of weapon-attack.ts because these are the pieces that OTHER rolled actions will reuse the
// moment they port (every damage roll in the engine explodes, and every attack can crit), and because
// the draw ORDER they define is the contract the whole client-truth RNG rests on.
//
// ⚠ THE DRAW ORDER FOR ONE ATTACK, end to end (verified against the PHP call order in
//   damage_type_roll.php — base dice line 487, explosion pass line 535, crit hook line 822, mitigation
//   line 1033 — and against the S1 golden):
//     1. the attack d20        — one die at 0 stacks, |stacks| + 1 otherwise
//     2. the trained-tier die  — "Skill Roll dN", drawn AFTER the d20
//     3. the base damage dice  — `dice` × d`die`, in index order
//     4. base explosion chains — per KEPT base die, in die order, each chain in link order
//     5. the crit bonus        — Rolled only: ONE uniform FRACTION, then each crit die followed
//                                immediately by its own explosion chain
//   Mitigation (resistance / immunity / vulnerability) draws nothing.

import type { RollSource } from './roll-source.js';
import { type Row, scalar } from '../ability/context.js';
import { intval } from '../php-compat/values.js';

export { type RollSource };

/** `roll_dice_arr` for ONE die, faithful to the branch PHP takes.
 *  At 0 stacks it draws once. Otherwise it draws |stacks| + 1 times and keeps the highest (advantage)
 *  or lowest (disadvantage) — every face is drawn, in order, before anything is kept. */
export function rollWithStacks(roll: RollSource, die: number, stacks: number): { kept: number; draws: Array<{ value: number; kept: boolean }> } {
  const count = stacks === 0 ? 1 : Math.abs(stacks) + 1;
  const values: number[] = [];
  for (let i = 0; i < count; i += 1) values.push(roll.d(die));
  const kept = stacks > 0 ? Math.max(...values) : Math.min(...values);
  let marked = false;
  const draws = values.map(value => {
    // Exactly ONE die is kept even when the pair tied, mirroring PHP's single surviving face.
    const isKept = !marked && value === kept;
    if (isKept) marked = true;
    return { value, kept: isKept };
  });
  return { kept, draws };
}

/**
 * The face a die at position `link` in an explosion chain must meet or beat
 * (`ft_exploding_dice_threshold_for_link`).
 *   · link 1 is the die actually rolled as part of the fragment — it always needs a strict natural max
 *     and is NEVER widened by Fortune.
 *   · link 2+ use the Fortune-widened, per-link-tightening threshold.
 *   · A return value greater than `die` means the chain is exhausted — that is what makes it terminate.
 *   · `max(2, …)` is a hard floor: face 1 NEVER explodes, at any Fortune.
 */
export function explosionThresholdForLink(die: number, fortune: number, link: number): number {
  if (link <= 1) return die;
  const widen = Math.max(0, fortune - 2); // Fortune below 2 collapses to the Fortune-2 baseline
  const start = Math.max(2, die - widen);
  return start + (link - 2);
}

export interface ExplosionLink {
  value: number;
  link: number;
  threshold: number;
}

/**
 * The explosion chain triggered by a single die that came up `firstValue`
 * (`ft_exploding_dice_extra_rolls`). One entry per ADDITIONAL die spawned, in chain order.
 * `hardCap` is a data-corruption backstop only — the decaying threshold already bounds the chain.
 */
export function explosionChain(roll: RollSource, firstValue: number, die: number, fortune: number, hardCap = 24): ExplosionLink[] {
  const out: ExplosionLink[] = [];
  // d1 always rolls its max (an unbounded chain); d2 leaves no non-exploding face above the face-1
  // floor. Neither can participate — guard independent of Fortune.
  if (die < 3) return out;
  // Clamp the seed to a face this die can actually show, so a summed total handed in by mistake
  // cannot satisfy link 1's strict-max test and mint a free die.
  let value = Math.max(1, Math.min(die, firstValue));
  let link = 1;
  while (out.length < hardCap) {
    const threshold = explosionThresholdForLink(die, fortune, link);
    if (threshold > die) break;   // chain exhausted — self-termination
    if (value < threshold) break; // this die did not explode
    const next = roll.d(die);
    out.push({ value: next, link: link + 1, threshold });
    value = next;
    link += 1;
  }
  return out;
}

export const CRIT_ROLLED_BUDGET_MULTIPLIER = 0.80;

/** The FRACTIONAL number of dice a Crit: Rolled bonus targets (`ft_crit_rolled_dice_target`). */
export function critRolledDiceTarget(declaredDice: number, die: number, multiplier = CRIT_ROLLED_BUDGET_MULTIPLIER): number {
  if (declaredDice < 1 || die < 1) return 0;
  return declaredDice * ((2 * die * multiplier) / (die + 1));
}

/**
 * How many dice a Crit: Rolled bonus actually rolls THIS time (`ft_crit_rolled_dice_count`).
 *
 * ⚠ The budget target is fractional and the remainder is settled by a RANDOM DRAW, not by rounding —
 * `rand() / getrandmax()` in PHP, i.e. a uniform FRACTION rather than a die face. This is the one place
 * in an attack that needs `RollSource.fraction()`, and it is drawn BEFORE any crit die. Over many crits
 * the mean equals the target exactly; on any single crit it is the floor or the ceiling.
 */
export function critRolledDiceCount(roll: RollSource, declaredDice: number, die: number, multiplier = CRIT_ROLLED_BUDGET_MULTIPLIER): number {
  const target = critRolledDiceTarget(declaredDice, die, multiplier);
  if (target <= 0) return 0;
  const whole = Math.floor(target);
  const remainder = target - whole;
  // Guard the draw itself: PHP only rolls when there IS a remainder, so an implementation that always
  // drew would consume a fraction the server never took and desynchronise every later draw.
  const bumped = remainder > 0 && roll.fraction() < remainder ? whole + 1 : whole;
  return Math.max(1, bumped);
}

export type CritVariant = 'maximum' | 'rolled' | 'doubled';

export interface CritBonus {
  variant: CritVariant;
  /** The bonus ADDED to the fragment's result. */
  total: number;
  /** Individual dice this bonus rolled, in draw order (Rolled only; the other models draw nothing). */
  tuples: Array<{ value: number; label: string; kind: 'critical' | 'explosion'; link?: number; threshold?: number }>;
}

/** check_critical_hit_logic: the strongest caster/root/host multiplier wins, with x2 as the floor. */
export function criticalHitDamageMultiplier(read: { value(row: Row, key: string): unknown }, caster: Row, host: Row): number {
  return Math.max(2, Number(intval(scalar(read.value(caster, 'critical_hit_damage_multiplier')))),
    Number(intval(scalar(caster.critical_hit_damage_multiplier))), Number(intval(scalar(read.value(host, 'critical_hit_damage_multiplier')))));
}

/**
 * The critical-hit bonus for one damage fragment (`ft_critical_hit_bonus`). All three campaign models:
 *
 *  · **maximum** (the default, and the model an un-opted campaign runs) — a flat `dice × die`. Draws
 *    NOTHING, so it never disturbs the draw stream.
 *  · **doubled** — the fragment's result AS IT STANDS at the crit hook, which already includes the
 *    rolled dice, their explosions, the roll modifier and the essence bonus. Draws nothing. Note this
 *    is a design statement, not a reskin: the flat model ignores modifiers entirely, so at high essence
 *    Doubled pulls sharply ahead.
 *  · **rolled** — a fractional dice budget settled by ONE uniform fraction, then that many dice, each
 *    of which can itself explode when the campaign's exploding-dice rule is on.
 */
export function criticalHitBonus(
  roll: RollSource,
  variant: CritVariant,
  fragment: { dice: number; die: number; rollResult: number },
  options: { canExplode: boolean; fortune: number; multiplier?: number },
): CritBonus {
  const { dice, die, rollResult } = fragment;
  const bonuses = Math.max(2, options.multiplier ?? 2) - 1;

  if (variant === 'doubled') {
    return { variant, total: rollResult * bonuses, tuples: [] };
  }

  if (variant === 'rolled') {
    let total = 0;
    const tuples: CritBonus['tuples'] = [];
    // Each bonus has its own fractional draw, then its dice/explosions, in legacy order.
    for (let bonus = 0; bonus < bonuses; bonus += 1) {
      const critDice = critRolledDiceCount(roll, dice, die);
      for (let n = 1; n <= critDice; n += 1) {
        const value = roll.d(Math.max(1, die));
        total += value;
        tuples.push({ value, label: `d${die} CRIT ${n}`, kind: 'critical' });
        if (!options.canExplode) continue;
        // Crit dice detonate under this variant deliberately — that synergy is the whole reason the
        // variant exists. Every other guard (die >= 3, the face-1 floor, self-termination) lives in the
        // chain helper.
        for (const extra of explosionChain(roll, value, die, options.fortune)) {
          total += extra.value;
          tuples.push({
            value: extra.value,
            label: `d${die} CRIT ${n} EXPLODE ${extra.link} (needed ${extra.threshold}+)`,
            kind: 'explosion',
            link: extra.link,
            threshold: extra.threshold,
          });
        }
      }
    }
    return { variant, total, tuples };
  }

  // Crit: Maximum (default)
  return { variant: 'maximum', total: dice * die * bonuses, tuples: [] };
}
